/**
 * src/engine/prng.ts — seedable PRNG (mulberry32) + Box–Muller normals (§6.18).
 * Deterministic for a fixed seed so Monte Carlo is reproducible and testable.
 */

/** mulberry32: fast 32-bit seedable uniform generator on [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface Rng {
  /** Uniform on [0, 1). */
  next(): number;
  /** Standard normal N(0, 1) via Box–Muller. */
  normal(): number;
}

/** Create a reproducible RNG with uniform and standard-normal draws. */
export function createRng(seed: number): Rng {
  const uniform = mulberry32(seed);
  let cached: number | null = null;
  return {
    next: uniform,
    normal(): number {
      if (cached !== null) {
        const v = cached;
        cached = null;
        return v;
      }
      // Avoid log(0): clamp u1 away from 0.
      const u1 = Math.max(uniform(), Number.EPSILON);
      const u2 = uniform();
      const r = Math.sqrt(-2 * Math.log(u1));
      cached = r * Math.sin(2 * Math.PI * u2);
      return r * Math.cos(2 * Math.PI * u2);
    },
  };
}
